import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import DepartmentAssign from "./DepartmentAssign";

function RequirementSet({ ownerType, ownerId, title, ctypes }) {
  const [reqs, setReqs] = useState([]);
  const [kind, setKind] = useState("qualification");
  const [label, setLabel] = useState("");
  const [opts, setOpts] = useState([]);
  const [condKey, setCondKey] = useState("min_age");
  const [condVal, setCondVal] = useState("");
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/config/licence-categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    api.get(`/requirements?owner_type=${ownerType}&owner_id=${ownerId}`).then((r) => setReqs(r.data)).catch(() => {});
  }, [ownerType, ownerId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try {
      const payload = { owner_type: ownerType, owner_id: ownerId, kind, label };
      if (kind === "qualification") {
        if (opts.length === 0) { toast.error("Select at least one qualification"); return; }
        payload.competency_type_ids = opts;
      } else {
        payload.condition_key = condKey;
        payload.condition_value = condKey === "min_age" ? { min_age: Number(condVal) } : condKey === "licence_category" ? { category: condVal } : {};
      }
      await api.post("/requirements", payload);
      toast.success("Requirement added"); setLabel(""); setOpts([]); setCondVal(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/requirements/${id}`); load(); } catch (e) { toast.error("Failed"); } };
  const toggleOpt = (id) => setOpts((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid={`reqset-${ownerType}`}>
      <div className="font-body font-semibold text-slate-800 mb-2">{title}</div>
      <div className="flex flex-col gap-1 mb-2">
        {reqs.length === 0 && <p className="text-slate-400 text-xs">No requirements — anyone qualified by insurance/licence may use it.</p>}
        {reqs.map((r) => (
          <div key={r.id} data-testid={`req-${r.id}`} className="flex items-center gap-2 text-sm bg-slate-50 rounded px-2 py-1">
            {r.kind === "qualification"
              ? <span className="font-body text-slate-700"><span className="font-semibold">{r.label || "Qualification"}</span>: {r.options.map((o) => o.name).join(" OR ")}</span>
              : <span className="font-body text-slate-700">Condition: {r.condition_key === "min_age" ? `Minimum age ${r.condition_value?.min_age}` : r.condition_key === "licence_category" ? `Licence ${r.condition_value?.category}` : "Insurer/supervisor approval"}</span>}
            <button data-testid={`del-req-${r.id}`} onClick={() => del(r.id)} className="ml-auto text-red-600"><Trash size={14} weight="bold" /></button>
          </div>
        ))}
        {reqs.length > 1 && <div className="text-[10px] uppercase font-bold text-slate-400">All clauses required (AND)</div>}
      </div>
      <div className="border-t border-slate-100 pt-2 flex flex-col gap-2">
        <select data-testid={`req-kind-${ownerType}`} className="h-9 border border-slate-300 rounded px-2 text-sm" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="qualification">Qualification (any of — OR / equivalency)</option>
          <option value="condition">Condition</option>
        </select>
        {kind === "qualification" ? (
          <>
            <Input data-testid={`req-label-${ownerType}`} className="h-9" placeholder="Clause label (e.g. Telehandler competency)" value={label} onChange={(e) => setLabel(e.target.value)} />
            <div className="max-h-32 overflow-y-auto border border-slate-200 rounded p-2 grid grid-cols-2 gap-1">
              {ctypes.map((ct) => (
                <label key={ct.id} className="flex items-center gap-1 text-xs">
                  <input type="checkbox" data-testid={`req-opt-${ct.id}`} checked={opts.includes(ct.id)} onChange={() => toggleOpt(ct.id)} /> {ct.name}
                </label>
              ))}
            </div>
          </>
        ) : (
          <div className="flex gap-2">
            <select data-testid={`req-cond-${ownerType}`} className="h-9 border border-slate-300 rounded px-2 text-sm" value={condKey} onChange={(e) => setCondKey(e.target.value)}>
              <option value="min_age">Minimum age</option><option value="insurer_approval">Insurer/supervisor approval</option><option value="licence_category">Licence category</option>
            </select>
            {condKey === "min_age" && <Input data-testid={`req-condval-${ownerType}`} className="h-9" placeholder="e.g. 21" value={condVal} onChange={(e) => setCondVal(e.target.value)} />}
            {condKey === "licence_category" && (
              <select data-testid={`req-condcat-${ownerType}`} className="h-9 border border-slate-300 rounded px-2 text-sm flex-1" value={condVal} onChange={(e) => setCondVal(e.target.value)}>
                <option value="">Select category…</option>
                {cats.map((c) => <option key={c.id} value={c.code}>{c.code} — {c.name}</option>)}
              </select>
            )}
          </div>
        )}
        <Button data-testid={`add-req-${ownerType}`} onClick={add} className="h-9 bg-slate-900 font-semibold self-start"><Plus size={14} weight="bold" className="mr-1" />Add requirement</Button>
      </div>
    </div>
  );
}

function InsuranceAssign({ asset }) {
  const [rules, setRules] = useState([]);
  const [assigns, setAssigns] = useState([]);
  const [ruleId, setRuleId] = useState("");
  const [scope, setScope] = useState("asset");
  const [isOverride, setIsOverride] = useState(false);

  const load = useCallback(() => {
    api.get(`/insurance/assignments?asset_id=${asset.id}`).then((r) => setAssigns(r.data)).catch(() => {});
  }, [asset.id]);
  useEffect(() => {
    load();
    api.get("/config/insurance-rules").then((r) => setRules(r.data)).catch(() => {});
  }, [load]);

  const add = async () => {
    if (!ruleId) { toast.error("Select a rule"); return; }
    try {
      const payload = { rule_id: ruleId, scope, is_override: isOverride };
      if (scope === "asset") payload.asset_id = asset.id;
      else if (scope === "asset_type") payload.asset_type_id = asset.asset_type_id;
      await api.post("/insurance/assignments", payload);
      toast.success("Rule assigned"); setRuleId(""); load();
    } catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/insurance/assignments/${id}`); load(); } catch (e) { toast.error("Failed"); } };

  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid="insurance-assign">
      <div className="font-body font-semibold text-slate-800 mb-2">Insurance Rules</div>
      <div className="flex flex-col gap-1 mb-2">
        {assigns.length === 0 && <p className="text-slate-400 text-xs">No insurance rules assigned to this vehicle.</p>}
        {assigns.map((a) => (
          <div key={a.id} data-testid={`assign-${a.id}`} className="flex items-center gap-2 text-sm bg-slate-50 rounded px-2 py-1">
            <span className="font-mono text-xs font-bold text-slate-700">{a.rule_code}</span>
            <span className="text-xs text-slate-500">{a.scope}{a.is_override ? " · override" : ""}</span>
            <button data-testid={`del-assign-${a.id}`} onClick={() => del(a.id)} className="ml-auto text-red-600"><Trash size={14} weight="bold" /></button>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-100 pt-2 flex flex-wrap gap-2 items-center">
        <select data-testid="assign-rule" className="h-9 border border-slate-300 rounded px-2 text-sm" value={ruleId} onChange={(e) => setRuleId(e.target.value)}>
          <option value="">Rule…</option>{rules.map((r) => <option key={r.id} value={r.id}>{r.rule_code}</option>)}
        </select>
        <select data-testid="assign-scope" className="h-9 border border-slate-300 rounded px-2 text-sm" value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="asset">This vehicle</option><option value="asset_type">This vehicle type</option><option value="fleet">Whole fleet</option>
        </select>
        <label className="flex items-center gap-1 text-xs"><input type="checkbox" data-testid="assign-override" checked={isOverride} onChange={(e) => setIsOverride(e.target.checked)} />Override inherited</label>
        <Button data-testid="add-assign" onClick={add} className="h-9 bg-slate-900 font-semibold"><Plus size={14} weight="bold" className="mr-1" />Assign</Button>
      </div>
    </div>
  );
}

function ActionPolicyEditor({ asset }) {
  const [policies, setPolicies] = useState({});
  const [saving, setSaving] = useState(false);
  const actions = [
    ["view", "View"], ["check", "Check / Inspect"], ["operate", "Operate / Drive"],
    ["maintain", "Maintain / Repair"], ["return_to_service", "Return to Service"],
  ];
  const defaults = { view: "allow", check: "warn", operate: "block", maintain: "override", return_to_service: "block" };

  const load = useCallback(() => {
    api.get("/config/asset-types").then((r) => {
      const t = r.data.find((x) => x.id === asset.asset_type_id);
      setPolicies((t && t.action_policies) || {});
    }).catch(() => {});
  }, [asset.asset_type_id]);
  useEffect(() => { if (asset.asset_type_id) load(); }, [load, asset.asset_type_id]);

  const setLevel = async (action, level) => {
    const next = { ...policies, [action]: level };
    setPolicies(next); setSaving(true);
    try {
      await api.put(`/config/asset-types/${asset.asset_type_id}`, { action_policies: next });
      toast.success("Policy saved");
    } catch (e) { toast.error("Failed to save policy"); load(); }
    finally { setSaving(false); }
  };

  if (!asset.asset_type_id) return null;
  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid="action-policy-editor">
      <div className="font-body font-semibold text-slate-800 mb-1">Eligibility action policies</div>
      <p className="text-slate-400 text-xs mb-2">How each action behaves for someone who is <em>not</em> recorded as authorised — applies to all {asset.asset_type_name || "assets of this type"}.</p>
      <div className="flex flex-col gap-1.5">
        {actions.map(([key, lbl]) => (
          <div key={key} className="flex items-center gap-2 text-sm">
            <span className="font-body text-slate-700 w-40">{lbl}</span>
            <select data-testid={`policy-${key}`} disabled={saving}
              className="h-8 border border-slate-300 rounded px-2 text-sm flex-1"
              value={policies[key] || defaults[key]} onChange={(e) => setLevel(key, e.target.value)}>
              <option value="allow">Allow</option>
              <option value="warn">Warning + self-acknowledge</option>
              <option value="override">Manager override required</option>
              <option value="block">Hard block</option>
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RequirementsManager({ asset }) {
  const [ctypes, setCtypes] = useState([]);
  useEffect(() => { api.get("/config/competency-types").then((r) => setCtypes(r.data)).catch(() => {}); }, []);
  return (
    <div className="flex flex-col gap-3">
      <DepartmentAssign entityType="asset" entityId={asset.id} label="this asset" />
      {asset.asset_type_id && <DepartmentAssign entityType="asset_type" entityId={asset.asset_type_id} label={`all ${asset.asset_type_name || "this type"}`} />}
      <ActionPolicyEditor asset={asset} />
      {asset.is_road_vehicle && <InsuranceAssign asset={asset} />}
      {asset.asset_type_id && <RequirementSet ownerType="asset_type" ownerId={asset.asset_type_id} title={`Requirements for all ${asset.asset_type_name || "this type"}`} ctypes={ctypes} />}
      <RequirementSet ownerType="asset" ownerId={asset.id} title="Additional requirements for this specific asset" ctypes={ctypes} />
    </div>
  );
}
